import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  AgxBodyText,
  AgxColumn,
  AgxDivider,
  AgxSelect,
  Images,
  MarketingItemType,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { LoadingState } from '../../../utils/loadingState';
import { formatCurrencyAsNumber } from '../../../utils/formatNumber';
import MarketingPackageList from './MarketingPackageList';
import { isEmpty } from 'lodash-es';
import { MarketingPackage, MarketingRow } from './types';
import { MarketingItem } from 'Api/Marketing/Types/Marketing';
import './MarketingPackages.scss';
import { ExternalIntegrations } from '@urbanx/agx.agencies.client';
import useGetPropertyDomainPrice from 'features/domain/useGetPropertyDomainPrice';

const realEstateProductName = 'realestate.com.au';
const domainProductName = 'domain.com.au';

const MarketingRowToItem = (item: MarketingRow): MarketingItem => {
  return {
    id: item.id,
    productName: item.productName,
    pricePerUnit: item.pricePerUnit ?? null,
    itemType: MarketingItemType.Custom,
    supplier: null,
    stockType: null,
  };
};

const isMarketingItem = (
  item: MarketingItem | null | undefined
): item is MarketingItem => item != null;

interface MarketingPackagesProps {
  id: string;
  onValueChanged: (value: { id: string; value: MarketingPackage }) => void;
  defaultValue?: MarketingPackage;
  validate?: boolean;
  hideNoMarketing?: boolean;
  required?: boolean;
}

const loadingMarketingPackagesOptions = [
  {
    id: 'Loading Marketing Packages...',
    value: 'Loading Marketing Packages...',
    label: 'Loading Marketing Packages...',
  },
];

const MarketingPackages = ({
  id,
  onValueChanged,
  defaultValue,
  validate,
  hideNoMarketing,
  required,
}: MarketingPackagesProps) => {
  const { loadingState: realEstateLoadingState, contract: realEstateContract } =
    useAppSelector(state => state.realEstate);
  const {
    loadingState: marketingPackagesLoadingState,
    packages: marketingPackages,
  } = useAppSelector(state => state.marketing.marketingPackages);
  const { loadingState: marketingItemsLoadingState, items: marketingItems } =
    useAppSelector(state => state.marketing.marketingItems);

  const externalIntegrations = useAppSelector(
    state => state.agencies.agencyInfo.info.externalIntegrations
  );

  const { reaEnabled, domainEnabled } = useMemo(() => {
    return {
      reaEnabled:
        externalIntegrations?.includes(ExternalIntegrations.RealEstateDotCom) ??
        false,
      domainEnabled:
        externalIntegrations?.includes(ExternalIntegrations.DomainDotCom) ??
        false,
    };
  }, [externalIntegrations]);

  const [selectedPackage, setSelectedPackage] = useState<MarketingPackage>(
    defaultValue ?? {
      packageId: 'NoMarketing',
      packageName: 'No Marketing',
      packageInclusions: [],
      packageAddons: [],
    }
  );

  const { domainListingFee, isDomainListingFeePending } =
    useGetPropertyDomainPrice();

  const isDropDownEnabled = useMemo(() => {
    return (
      marketingPackagesLoadingState === LoadingState.Loaded &&
      marketingItemsLoadingState === LoadingState.Loaded &&
      (!reaEnabled ||
        (realEstateLoadingState !== LoadingState.Loading &&
          realEstateLoadingState !== LoadingState.NotLoaded)) &&
      (!domainEnabled || (domainEnabled && !isDomainListingFeePending))
    );
  }, [
    marketingPackagesLoadingState,
    marketingItemsLoadingState,
    reaEnabled,
    realEstateLoadingState,
    domainEnabled,
    isDomainListingFeePending,
    selectedPackage,
  ]);

  const [availablePackageInclusions, setAvailablePackageInclusions] = useState<
    MarketingItem[]
  >([]);
  const [availablePackageAddons, setAvailablePackageAddons] = useState<
    MarketingItem[]
  >([]);
  const [customPackageItems, setCustomPackageItems] = useState<MarketingItem[]>(
    []
  );
  const [showSelectDomain, setShowSelectDomain] = useState(false);
  const [showSelectRealEstate, setShowSelectRealEstate] = useState(false);

  useEffect(() => {
    if (customPackageItems?.length === 0 && marketingItems?.length > 0) {
      const customPackageAddOns = selectedPackage?.packageAddons
        ?.map(packageAddon => {
          const marketingItem = marketingItems.find(
            item => packageAddon.id === item.id
          );
          if (!marketingItem) return MarketingRowToItem(packageAddon);
          return null;
        })
        .filter(isMarketingItem);

      const customPackageInclusions = selectedPackage?.packageInclusions
        ?.map(packageInclusion => {
          const marketingItem = marketingItems.find(
            item => packageInclusion.id === item.id
          );
          if (!marketingItem) return MarketingRowToItem(packageInclusion);
          return null;
        })
        .filter(isMarketingItem);

      const allCustomPackageInclusions =
        customPackageInclusions?.map(item => ({
          ...item,
          itemType: MarketingItemType.Custom,
        })) || [];
      const allCustomPackageAddons =
        customPackageAddOns?.map(item => ({
          ...item,
          itemType: MarketingItemType.Custom,
        })) || [];

      setCustomPackageItems([
        ...allCustomPackageInclusions,
        ...allCustomPackageAddons,
      ]);
    }
  }, [marketingItems]);

  useEffect(() => {
    onValueChanged({
      id,
      value: selectedPackage,
    });
  }, [selectedPackage]);

  const addCustomMarketingItemRow = useCallback(
    (isInclusion: boolean) => {
      var customItemId = `${isInclusion ? 'inclusion-' : 'addon-'}${uuidv4()}`;

      const newCustomItem = {
        id: customItemId,
        productName: '',
        pricePerUnit: null,
        itemType: MarketingItemType.Custom,
        value: true,
        supplier: null,
        stockType: null,
      } as MarketingItem;

      const newCustomRow = {
        id: customItemId,
        productName: '',
        pricePerUnit: undefined,
        quantity: undefined,
      } as MarketingRow;

      const updatedPackage = {
        ...selectedPackage,
        packageInclusions: isInclusion
          ? [...selectedPackage.packageInclusions, newCustomRow]
          : selectedPackage.packageInclusions,
        packageAddons: isInclusion
          ? selectedPackage.packageAddons
          : [...selectedPackage.packageAddons, newCustomRow],
      };

      setSelectedPackage(updatedPackage);
      setCustomPackageItems(prev => [...prev, { ...newCustomItem }]);
    },
    [selectedPackage, setSelectedPackage, setCustomPackageItems]
  );

  const deleteCustomMarketingItemRow = useCallback(
    (item: MarketingRow) => {
      const isInclusion = item.id.includes('inclusion-') ? true : false;

      let allCustomPackageItems = [...customPackageItems];
      const index = allCustomPackageItems.findIndex(
        packageItm => packageItm.id === item.id
      );
      allCustomPackageItems.splice(index, 1);

      const updatedPackage = {
        ...selectedPackage,
        packageAddons: isInclusion
          ? selectedPackage.packageAddons
          : selectedPackage.packageAddons.filter(addOn => addOn.id !== item.id),
        packageInclusions: isInclusion
          ? selectedPackage.packageInclusions.filter(inc => inc.id !== item.id)
          : selectedPackage.packageInclusions,
      };

      setSelectedPackage(updatedPackage);
      setCustomPackageItems(allCustomPackageItems);
    },
    [selectedPackage, setSelectedPackage, setCustomPackageItems]
  );

  useEffect(() => {
    const customPackageItemsInclusions = customPackageItems.filter(
      customPackageItem => customPackageItem?.id?.includes('inclusion-')
    );
    const customPackageItemsAddons = customPackageItems.filter(
      customPackageItem => customPackageItem?.id?.includes('addon-')
    );

    if (selectedPackage?.packageId === 'CustomMarketing') {
      // Custom marketing we want to display all items as inclusion with no items checked
      setAvailablePackageInclusions([
        ...marketingItems,
        ...customPackageItemsInclusions,
      ]);
      setAvailablePackageAddons([...customPackageItemsAddons]);
      setShowSelectRealEstate(
        reaEnabled && realEstateLoadingState !== LoadingState.NotLoaded
      );
      setShowSelectDomain(domainEnabled && !isDomainListingFeePending);
    } else if (selectedPackage?.packageId === 'NoMarketing') {
      // No marketing we want to display no items and clear anything checked
      setAvailablePackageInclusions([]);
      setAvailablePackageAddons([]);
      setShowSelectRealEstate(false);
      setShowSelectDomain(false);
    } else {
      // Marketing packages we only want to display the inclusions and addons assigned to the marketing package
      const marketingPackage = marketingPackages.find(
        mp => mp.id === selectedPackage?.packageId
      );

      if (marketingPackage == null) return;

      const hasRealEstateListing =
        reaEnabled &&
        marketingPackage?.includeRealEstateListing &&
        realEstateLoadingState !== LoadingState.NotLoaded;
      const hasDomainListing =
        domainEnabled &&
        marketingPackage?.includeDomainListing &&
        !isDomainListingFeePending;

      const marketingPackageItems = marketingPackage?.items
        ?.map(id => marketingItems.find(item => item.id === id))
        .filter(isMarketingItem);
      const marketingPackageAddons = marketingPackage?.addOns
        ?.map(id => marketingItems.find(item => item.id === id))
        .filter(isMarketingItem);

      setShowSelectRealEstate(hasRealEstateListing);
      setShowSelectDomain(hasDomainListing);
      setAvailablePackageInclusions([
        ...(marketingPackageItems ?? []),
        ...customPackageItemsInclusions,
      ]);
      setAvailablePackageAddons([
        ...(marketingPackageAddons ?? []),
        ...customPackageItemsAddons,
      ]);
    }
  }, [selectedPackage, customPackageItems]);

  const dropDownOptions = useMemo(() => {
    const result = [
      ...marketingPackages?.map(
        ({
          items,
          name,
          id,
          includeDomainListing,
          includeRealEstateListing,
        }) => {
          const labelSubtotal =
            items?.reduce(
              (acc, itemName) =>
                acc +
                (marketingItems?.find(i => i.id === itemName)?.pricePerUnit ??
                  0),
              0
            ) +
            (includeRealEstateListing && reaEnabled
              ? (realEstateContract?.price ?? 0)
              : 0) +
            (includeDomainListing && domainEnabled
              ? (domainListingFee ?? 0)
              : 0);

          return {
            id: id,
            value: id,
            label: `${name} - $${formatCurrencyAsNumber(labelSubtotal?.toString())}`,
            isPlaceholderOption: false,
          };
        }
      ),
      {
        id: 'CustomMarketing',
        value: 'CustomMarketing',
        label: 'Custom Marketing',
        isPlaceholderOption: false,
      },
      {
        ...(hideNoMarketing
          ? {
              id: 'Choose',
              value: 'Choose',
              label: 'Select Marketing Package',
              isPlaceholderOption: true,
            }
          : {
              id: 'NoMarketing',
              value: 'NoMarketing',
              label: 'No Marketing',
              isPlaceholderOption: false,
            }),
      },
    ];

    return result;
  }, [
    marketingPackages,
    marketingItems,
    realEstateContract,
    domainListingFee,
    reaEnabled,
    domainEnabled,
    hideNoMarketing,
  ]);

  const onMarketingPackageChanged = useCallback(
    (packageId: string) => {
      const isPlaceholderOption =
        dropDownOptions.find(o => o.value === packageId)?.isPlaceholderOption ??
        false;

      if (
        packageId == null ||
        selectedPackage?.packageId === packageId ||
        !isDropDownEnabled ||
        isPlaceholderOption
      ) {
        return;
      }

      if (packageId === 'CustomMarketing') {
        // Custom marketing we want to display all items as inclusion with no items checked
        const updatedSelectedPackage = {
          packageId,
          packageName: 'Custom Marketing',
          packageInclusions: [],
          packageAddons: [],
        };

        setSelectedPackage(updatedSelectedPackage);
      } else if (packageId === 'NoMarketing') {
        // No marketing we want to display no items and clear anything checked
        const updatedSelectedPackage = {
          packageId,
          packageName: 'No Marketing',
          packageInclusions: [],
          packageAddons: [],
        };

        setSelectedPackage(updatedSelectedPackage);
      } else {
        // Marketing packages we only want to display the inclusions and addons assigned to the marketing package
        const marketingPackage = marketingPackages.find(
          mp => mp.id === packageId
        );

        if (marketingPackage == null) return;

        const hasRealEstateListing =
          marketingPackage?.includeRealEstateListing && reaEnabled;
        const hasDomainListing =
          marketingPackage?.includeDomainListing && domainEnabled;
        const marketingPackageItems = marketingItems.filter(mi =>
          marketingPackage?.items?.find(i => i === mi.id)
        );

        const updatedSelectedPackage = {
          packageId,
          packageName: marketingPackage.name,
          packageInclusions: [
            ...(hasRealEstateListing
              ? [
                  {
                    id: realEstateProductName,
                    productName: realEstateProductName,
                    pricePerUnit: realEstateContract?.price,
                  },
                ]
              : []),
            ...(hasDomainListing
              ? [
                  {
                    id: domainProductName,
                    productName: domainProductName,
                    pricePerUnit: domainListingFee,
                  },
                ]
              : []),
            ...marketingPackageItems.map(item => ({
              id: item.id,
              productName: item.productName,
              pricePerUnit: item.pricePerUnit ?? undefined,
            })),
          ],
          packageAddons: [],
        };

        setSelectedPackage(updatedSelectedPackage);
      }
    },
    [
      dropDownOptions,
      selectedPackage,
      isDropDownEnabled,
      marketingPackages,
      marketingItems,
      setSelectedPackage,
      reaEnabled,
      domainEnabled,
      realEstateContract,
      domainListingFee,
    ]
  );

  useEffect(() => {
    // If the domain or realestate price has changed, then update the price in the selected package
    if (
      selectedPackage?.packageInclusions?.some(
        inc =>
          (inc.productName === realEstateProductName &&
            inc.pricePerUnit !== realEstateContract?.price) ||
          (inc.productName === domainProductName &&
            inc.pricePerUnit !== domainListingFee)
      )
    ) {
      setSelectedPackage(prev => ({
        ...prev,
        packageInclusions: prev?.packageInclusions?.map(inc => {
          if (inc.productName === realEstateProductName) {
            return {
              ...inc,
              pricePerUnit: realEstateContract?.price,
            };
          } else if (inc.productName === domainProductName) {
            return {
              ...inc,
              pricePerUnit: domainListingFee,
            };
          } else {
            return inc;
          }
        }),
      }));
    }
  }, [realEstateContract?.price, domainListingFee]);

  useEffect(() => {
    if (
      !defaultValue ||
      (!availablePackageInclusions?.length &&
        !availablePackageAddons?.length) ||
      (!defaultValue?.packageInclusions?.length &&
        !defaultValue?.packageAddons?.length)
    ) {
      return;
    }

    let hasChanges = false;
    let updatedPackage = { ...defaultValue };

    // Check for any inclusions that are no longer available
    const validInclusions =
      defaultValue.packageInclusions?.filter(inclusion => {
        if (
          inclusion.id === domainProductName ||
          inclusion.id === realEstateProductName ||
          inclusion.id.startsWith('inclusion-')
        )
          return true;

        return availablePackageInclusions.some(
          available => available.id === inclusion.id
        );
      }) ?? [];

    // Check for any addons that are no longer available
    if (validInclusions.length !== defaultValue.packageInclusions?.length) {
      updatedPackage.packageInclusions = validInclusions;
      hasChanges = true;
    }

    const validAddons =
      defaultValue.packageAddons?.filter(addon => {
        if (addon.id.startsWith('addon-')) return true;

        return availablePackageAddons.some(
          available => available.id === addon.id
        );
      }) ?? [];

    if (validAddons.length !== defaultValue.packageAddons?.length) {
      updatedPackage.packageAddons = validAddons;
      hasChanges = true;
    }

    if (hasChanges) {
      onSelectProductsChange(updatedPackage);
    }
  }, [availablePackageInclusions, availablePackageAddons]);

  const onSelectProductsChange = useCallback(
    (updatedSelectedPackage: MarketingPackage) => {
      setSelectedPackage(updatedSelectedPackage);
      onValueChanged({
        id,
        value: updatedSelectedPackage,
      });
    },
    [id, onValueChanged, setSelectedPackage]
  );

  const getDefaultValue = useCallback(() => {
    if (isEmpty(selectedPackage)) {
      return dropDownOptions
        ? dropDownOptions[dropDownOptions.length - 1]
        : null;
    }

    return {
      id: selectedPackage?.packageId,
      value: selectedPackage?.packageId,
      label: selectedPackage?.packageName,
    };
  }, [selectedPackage, dropDownOptions]);

  return (
    <>
      <AgxColumn fill>
        <AgxColumn extraClasses="marketingPackageSelector">
          <AgxSelect
            key={`${id}-${isDropDownEnabled}`}
            id={id}
            label="Marketing Package"
            required
            options={
              isDropDownEnabled
                ? dropDownOptions
                : loadingMarketingPackagesOptions
            }
            defaultValue={
              isDropDownEnabled
                ? getDefaultValue()
                : 'Loading Marketing Packages...'
            }
            hideOptionalLabel={true}
            onValueChanged={({ value }) => onMarketingPackageChanged(value)}
            dataTestId={`agx-${id}`}
            error={
              validate && required && isEmpty(selectedPackage)
                ? 'Please select marketing package'
                : undefined
            }
          />
        </AgxColumn>
        {isDropDownEnabled &&
          selectedPackage?.packageId &&
          selectedPackage?.packageId !== 'NoMarketing' && (
            <AgxDivider expanded />
          )}
        {isDropDownEnabled &&
          selectedPackage?.packageId &&
          selectedPackage?.packageId !== 'NoMarketing' && (
            <MarketingPackageList
              key={`marketingPackageList-${selectedPackage?.packageId}-${isDropDownEnabled}`}
              selectedPackage={selectedPackage}
              showSelectRealEstate={showSelectRealEstate}
              showSelectDomain={showSelectDomain}
              availablePackageInclusions={availablePackageInclusions}
              availablePackageAddons={availablePackageAddons}
              realEstateProductName={realEstateProductName}
              domainProductName={domainProductName}
              onSelectProductsChange={onSelectProductsChange}
              addCustomMarketingItemRow={addCustomMarketingItemRow}
              deleteCustomMarketingItemRow={deleteCustomMarketingItemRow}
              validate={validate}
            />
          )}
      </AgxColumn>
      {validate &&
        selectedPackage?.packageId != 'NoMarketing' &&
        !selectedPackage?.packageInclusions?.length &&
        !selectedPackage?.packageAddons?.length && (
          <>
            <AgxDivider expanded />
            <AgxColumn centered extraClasses="marketingPackageErrors">
              <div className="errorMessage">
                <Images.AlertCircle />
                <AgxBodyText
                  small
                  extraClasses="error"
                  dataTestId={`error-${id}`}
                >
                  Please select at least one item to proceed
                </AgxBodyText>
              </div>
              {!hideNoMarketing && (
                <AgxBodyText small>
                  Not using marketing? Switch to "No Marketing" to continue.
                </AgxBodyText>
              )}
            </AgxColumn>
          </>
        )}
    </>
  );
};

export default MarketingPackages;
